/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useEffect, useRef } from "react";
import { useEventCallback, useForkRef } from "../../../hooks";
import { getOwnerDocument } from "../../../utils";

type ClickAwayMouseEventHandler = "onClick" | "onMouseDown" | "onMouseUp";
type ClickAwayTouchEventHandler = "onTouchStart" | "onTouchEnd";

function mapEventPropToEvent<
	EventHandlerName extends ClickAwayMouseEventHandler | ClickAwayTouchEventHandler
>(
	eventProp: EventHandlerName
): EventHandlerName extends `on${infer EventName}` ? Lowercase<EventName> : never {
	return eventProp.substring(2).toLowerCase() as any;
}

function clickedRootScrollbar(event: MouseEvent, doc: Document): boolean {
	return (
		doc.documentElement.clientWidth < event.clientX || doc.documentElement.clientHeight < event.clientY
	);
}

export interface IClickAwayListenerProps {
	children: React.ReactElement;
	disableReactTree?: boolean;
	mouseEvent?: ClickAwayMouseEventHandler | false;
	onClickAway: (event: MouseEvent | TouchEvent) => void;
	touchEvent?: ClickAwayTouchEventHandler | false;
}

export const ClickAwayListener: React.FC<IClickAwayListenerProps> = ({
	children,
	disableReactTree,
	mouseEvent,
	onClickAway,
	touchEvent
}) => {
	const movedRef = useRef(false);
	const nodeRef = useRef<Element>(null);
	const activatedRef = useRef(false);
	const syntheticEventRef = useRef(false);

	const forkRef = useForkRef(children["ref"], nodeRef);

	const handleClickAway = useEventCallback((event: MouseEvent | TouchEvent) => {
		const insideReactTree = syntheticEventRef.current;
		syntheticEventRef.current = false;

		const doc = getOwnerDocument(nodeRef.current);

		if (
			!activatedRef.current ||
			!nodeRef.current ||
			("clientX" in event && clickedRootScrollbar(event, doc))
		) {
			return;
		}

		// Do not act if user performed touchmove
		if (movedRef.current) {
			movedRef.current = false;
			return;
		}

		let insideDOM;

		if (event.composedPath) {
			insideDOM = event.composedPath().indexOf(nodeRef.current) > -1;
		} else {
			insideDOM =
				!doc.documentElement.contains(event.target as Node) ||
				nodeRef.current.contains(event.target as Node);
		}

		if (!insideDOM && (disableReactTree || !insideReactTree)) {
			onClickAway(event);
		}
	});

	const createHandleSynthetic = (handlerName: string) => (event: React.SyntheticEvent) => {
		syntheticEventRef.current = true;

		const childrenPropsHandler = children.props[handlerName];
		if (childrenPropsHandler) {
			childrenPropsHandler(event);
		}
	};

	const childrenProps: { ref: React.Ref<Element> } & Pick<
		React.DOMAttributes<Element>,
		ClickAwayMouseEventHandler | ClickAwayTouchEventHandler
	> = { ref: forkRef };

	if (touchEvent !== false) {
		childrenProps[touchEvent] = createHandleSynthetic(touchEvent);
	}

	useEffect(() => {
		setTimeout(() => {
			activatedRef.current = true;
		}, 0);

		return () => {
			activatedRef.current = false;
		};
	}, []);

	useEffect(() => {
		if (touchEvent !== false) {
			const mappedTouchEvent = mapEventPropToEvent(touchEvent);
			const doc = getOwnerDocument(nodeRef.current);

			const handleTouchMove = () => {
				movedRef.current = true;
			};

			doc.addEventListener(mappedTouchEvent, handleClickAway);
			doc.addEventListener("touchmove", handleTouchMove);

			return () => {
				doc.removeEventListener(mappedTouchEvent, handleClickAway);
				doc.removeEventListener("touchmove", handleTouchMove);
			};
		}

		return undefined;
	}, [ handleClickAway, touchEvent ]);

	if (mouseEvent !== false) {
		childrenProps[mouseEvent] = createHandleSynthetic(mouseEvent);
	}

	useEffect(() => {
		if (mouseEvent !== false) {
			const mappedMouseEvent = mapEventPropToEvent(mouseEvent);
			const doc = getOwnerDocument(nodeRef.current);

			doc.addEventListener(mappedMouseEvent, handleClickAway);

			return () => {
				doc.removeEventListener(mappedMouseEvent, handleClickAway);
			};
		}

		return undefined;
	}, [ handleClickAway, mouseEvent ]);

	return <>{React.cloneElement(children, childrenProps)}</>;
};

ClickAwayListener.defaultProps = {
	mouseEvent: "onClick",
	touchEvent: "onTouchEnd"
};
